import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link as RouterLink, browserHistory } from 'react-router';
import Sticky from 'react-stickynode';
import _pick from 'lodash/pick';
import { ButtonV2, Link } from '@zola/zola-ui';
import { trackOnboardingCompleted, onboardTrackingConstants } from '@zola-helpers/client';
import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';

import { NAV_DATA } from 'constants/navConstants';
import SecondaryNavV2 from 'components/common/header/SecondaryNavV2';

// components
import SingleSampleAccessPoint from 'cards/components/common/SingleSample/SingleSampleAccessPoint/SingleSampleAccessPoint';
import { APWebsitePdp } from 'cards/components/common/SingleSample/SingleSampleAccessPoint/SingleSampleAccessPointUI';

import featureFlags from '../../../util/featureFlags';
import ThemeSwatches from './ThemeSwatches';
import SelectAndCustomizeButton from '../common/SelectAndCustomizeButton';
import ConnectedRelatedCardsCarousel from '../../../cards/components/common/RelatedCardsCarousel/RelatedCardsCarousel';
import SecondaryNav from '../../common/header/SecondaryNav/SecondaryNav';
import CardsModalRoot from '../../../cards/components/common/CardsModalRoot';

// actions
import {
  getByKeyV2,
  resetThemes,
  storeCurrentThemeKey,
} from '../../../actions/website/ThemeActions';
import {
  updateWeddingTheme,
  fetchWeddingAccount,
  disableWebsiteOnboarding,
} from '../../../actions/WeddingActions';
import { showModal } from '../../../actions/ModalActions';
import { weddingWebsiteNavItems } from '../../common/header/SecondaryNav/secondaryNavData';
import useScrollPosition from '../../../lib/hooks/useScrollPosition';

// css & assets
import '../../../stylesheets/pages/components/ThemeDetail.less';
import ChromeBar from '../../../assets/images/icons/icon-chrome.svg';
import { getUserWeddingDate } from '../../../selectors/user/userSelectors';

const { SECONDARY } = NAV_DATA;

/**
 * Keeps a space of 30px from the top navbar.
 * The 30px are based on the space between the two columns.
 * Having the same space creates a symmetrical effect.
 */
function getSidePaneStickyTopPosition(direction) {
  switch (direction) {
    case 'up':
      return 202;
    case 'down':
      return 134;
    default:
      return 0;
  }
}

const ThemeDetail = ({
  currentThemeFromState,
  onboard,
  themeGroup,
  weddingDate,
  slug,
  pageKey,
  shareInfo,
  weddingTheme,
  loadTheme,
  unloadTheme,
  storeThemeKey,
  setWeddingTheme,
  searchFilterColor,
}) => {
  const [localCurrentTheme, setLocalCurrentTheme] = useState(currentThemeFromState);
  const [relatedCardsCarouselStatus, setRelateCardsCarouselStatus] = useState(true);
  const [choseNewTheme, updateChoseNewTheme] = useState(false);
  const [sidePaneStickyTopPosition, setSidePaneStickyTopPosition] = useState(0);
  const { direction } = useScrollPosition();

  useEffect(() => {
    if (!themeGroup.themes) loadTheme();
  }, [themeGroup, loadTheme]);

  useEffect(() => {
    return () => {
      if (localCurrentTheme) {
        const { key } = localCurrentTheme;
        storeThemeKey(key);
      }
      unloadTheme();
    };
  }, []); // // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!localCurrentTheme && themeGroup.themes) {
      setLocalCurrentTheme(themeGroup.themes[0]);
    } else if (currentThemeFromState) {
      setLocalCurrentTheme(currentThemeFromState);
    }
    setSidePaneStickyTopPosition(getSidePaneStickyTopPosition(direction));
  }, [themeGroup, localCurrentTheme, currentThemeFromState, direction]);

  const changeThemeView = theme => setLocalCurrentTheme(theme);

  const handleSelect = themeId => {
    updateChoseNewTheme(true);
    setWeddingTheme(themeId, onboard, weddingDate);
  };

  const renderMarketingModule = () => {
    return (
      <RouterLink to="/wedding-planning/save-the-date/shop">
        <div className="marketing-module" />
      </RouterLink>
    );
  };

  const renderRelatedCardsCarousel = () => {
    if (!(localCurrentTheme && themeGroup && themeGroup.title) || !relatedCardsCarouselStatus) {
      return renderMarketingModule();
    }

    return (
      <div className="related-cards-carousel">
        <div className="related-cards-carousel__section">
          <ConnectedRelatedCardsCarousel
            family={themeGroup.title}
            filterColor={searchFilterColor[0]}
            initialVariationColor={localCurrentTheme.swatch_color_1}
            referenceType="website"
            lightView
            fallbackDisplay={() => setRelateCardsCarouselStatus(false)}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      {!onboard &&
        (featureFlags.get('navRedesign') ? (
          <SecondaryNavV2 activeLinkId={SECONDARY.WEBSITE.DESIGNS} />
        ) : (
          <div>
            <span className="visible-md-block visible-lg-block">
              <SecondaryNav
                slug={slug}
                pageKey={pageKey}
                shareInfo={shareInfo}
                navItems={weddingWebsiteNavItems}
                altActive
              />
            </span>
            <span className="hidden-md hidden-lg">
              <SecondaryNav
                slug={slug}
                pageKey={pageKey}
                shareInfo={shareInfo}
                navItems={weddingWebsiteNavItems}
                altActive
              />
            </span>
          </div>
        ))}
      <div>
        <div id="themes-nav-wrapper">
          <div className="content-top">
            <div>
              <div className="nav navbar-nav navbar-left">
                <RouterLink
                  to="/wedding/manage/website/designs"
                  className="themes-detail-back zola-ui link link-arrow prefix"
                >
                  Back To All Designs
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid theme-details theme-detail-postauth-v2 theme-detail-postauth sticky-boundary">
        {localCurrentTheme && (
          <div className="row">
            <div className="col-xs-12 visible-xs">
              <div className="theme-wrapper">
                {localCurrentTheme.id === weddingTheme.id && (
                  <span className="zolaicon-checkmark theme-title_selected-icon" />
                )}
                <h3 className="theme-title">
                  {themeGroup.title}{' '}
                  {themeGroup.is_new && <div className="zola-ui tag tag--sm tag--primary">NEW</div>}
                </h3>
              </div>
              {themeGroup.partner_name && (
                <p className="theme-detail-designedby">
                  Designed by{' '}
                  {themeGroup.partner_url ? (
                    <Link href={themeGroup.partner_url}>{themeGroup.partner_name}</Link>
                  ) : (
                    <span>{themeGroup.partner_name}</span>
                  )}
                </p>
              )}
              <p className="theme-detail-description">{themeGroup.description}</p>
              <h5 className="text-sans theme-swatch-heading">COLOR</h5>
              <div className="swatches-partner-container">
                <ThemeSwatches
                  themeOptions={themeGroup}
                  onSwatchClick={changeThemeView}
                  currentTheme={localCurrentTheme}
                  pageName="theme-detail"
                />
                {themeGroup.partner_cta_label && (
                  <p>
                    {themeGroup.partner_cta_label}{' '}
                    {themeGroup.partner_cta_url ? (
                      <Link href={themeGroup.partner_cta_url}>{themeGroup.partner_name}</Link>
                    ) : (
                      <span>{themeGroup.partner_name}</span>
                    )}
                  </p>
                )}
              </div>
              {themeGroup.partner_cta_label && (
                <p>
                  {themeGroup.partner_cta_label}{' '}
                  {themeGroup.partner_cta_url ? (
                    <Link href={themeGroup.partner_cta_url}>{themeGroup.partner_name}</Link>
                  ) : (
                    <span>{themeGroup.partner_name}</span>
                  )}
                </p>
              )}
              <div className="actions">
                <SelectAndCustomizeButton
                  currentTheme={localCurrentTheme}
                  weddingTheme={weddingTheme}
                  clickHandler={handleSelect}
                  onboard={onboard}
                  choseNewTheme={choseNewTheme}
                />
                <ButtonV2
                  component="a"
                  role="link"
                  variant="secondary"
                  className="view-details__button"
                  href={`/wedding/sample-${localCurrentTheme.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See a Preview
                </ButtonV2>
              </div>
            </div>
            <div className="col-sm-7 col-sm-triple-gutter-left col-xs-12 theme-preview-container">
              {window.zola.env !== 'production' && (
                <img
                  src={ChromeBar}
                  alt="icon chrome"
                  className="img-responsive center-block icon-chrome"
                />
              )}
              <div className="big-preview">
                <img
                  src={localCurrentTheme.full_preview_image}
                  alt={themeGroup.name}
                  className="img-responsive center-block"
                />
              </div>
            </div>
            <div className="col-sm-5 col-sm-triple-gutter-right hidden-xs">
              <Sticky
                top={sidePaneStickyTopPosition}
                bottomBoundary=".sticky-boundary"
                enabled={isDesktop()}
              >
                <h3 className="h1 theme-title-v2">
                  {localCurrentTheme.id === weddingTheme.id && (
                    <span className="zolaicon-checkmark theme-title_selected-icon" />
                  )}
                  {themeGroup.title}{' '}
                  {themeGroup.is_new && <div className="zola-ui tag tag--sm tag--primary">NEW</div>}
                </h3>
                {themeGroup.partner_name && (
                  <p className="theme-detail-designedby">
                    Designed by{' '}
                    {themeGroup.partner_url ? (
                      <Link href={themeGroup.partner_url} target="_blank" rel="noopener noreferrer">
                        {themeGroup.partner_name}
                      </Link>
                    ) : (
                      <span>{themeGroup.partner_name}</span>
                    )}
                  </p>
                )}
                <p className="theme-detail-description">{themeGroup.description}</p>
                {themeGroup.partner_cta_label && (
                  <p>
                    {themeGroup.partner_cta_label}{' '}
                    {themeGroup.partner_cta_url ? (
                      <Link
                        href={themeGroup.partner_cta_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {themeGroup.partner_name}
                      </Link>
                    ) : (
                      <span>{themeGroup.partner_name}</span>
                    )}
                  </p>
                )}
                <div>
                  <div className="color__wrapper">
                    <h5 className="text-sans theme-swatch-heading">COLOR</h5>
                    <div className="swatches-partner-container">
                      <ThemeSwatches
                        themeOptions={themeGroup}
                        onSwatchClick={changeThemeView}
                        currentTheme={localCurrentTheme}
                        pageName="theme-detail"
                      />
                      {themeGroup.partner_cta_label && (
                        <p>
                          {themeGroup.partner_cta_label}{' '}
                          {themeGroup.partner_cta_url ? (
                            <Link href={themeGroup.partner_cta_url}>{themeGroup.partner_name}</Link>
                          ) : (
                            <span>{themeGroup.partner_name}</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="actions__wrapper">
                    <div className="actions">
                      <SelectAndCustomizeButton
                        currentTheme={localCurrentTheme}
                        weddingTheme={weddingTheme}
                        clickHandler={handleSelect}
                        onboard={onboard}
                        choseNewTheme={choseNewTheme}
                      />
                      <ButtonV2
                        component="a"
                        variant="secondary"
                        role="link"
                        href={`/wedding/sample-${localCurrentTheme.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-details__button"
                      >
                        See a Preview
                      </ButtonV2>
                    </div>
                  </div>
                </div>
                {renderRelatedCardsCarousel()}
                {featureFlags.get('singleSampleWebsiteAccessPoint') && (
                  <SingleSampleAccessPoint
                    familyName={localCurrentTheme.key}
                    displayComponent={<APWebsitePdp />}
                  />
                )}
              </Sticky>
            </div>
            <div className="col-xs-12 visible-xs is-mobile last-section">
              {renderRelatedCardsCarousel()}
              {featureFlags.get('singleSampleWebsiteAccessPoint') && (
                <SingleSampleAccessPoint
                  familyName={localCurrentTheme.key}
                  displayComponent={<APWebsitePdp />}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <CardsModalRoot />
    </div>
  );
};

ThemeDetail.propTypes = {
  themeGroup: PropTypes.shape({
    themes: PropTypes.arrayOf(PropTypes.shape({})),
    currentTheme: PropTypes.shape({}),
    title: PropTypes.string,
  }),
  currentThemeFromState: PropTypes.shape({
    theme: PropTypes.shape({}),
  }),
  weddingTheme: PropTypes.shape({}),
  loadTheme: PropTypes.func.isRequired,
  unloadTheme: PropTypes.func.isRequired,
  setWeddingTheme: PropTypes.func.isRequired,
  onboard: PropTypes.bool,
  storeThemeKey: PropTypes.func,
  slug: PropTypes.string,
  shareInfo: PropTypes.shape({}),
  pageKey: PropTypes.string,
  searchFilterColor: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = state => ({
  themeGroup: state.website.themeListing.themeDetails,
  weddingTheme: state.wedding.theme,
  onboard: !state.wedding.account.disable_website_onboarding,
  currentThemeFromState: state.website.themeListing.currentTheme.theme,
  slug: state.wedding.wedding.slug,
  shareInfo: Object.assign({}, _pick(state.wedding.account, 'enable_passcode', 'passcode')),
  published: state.wedding.account.published,
  searchFilterColor: state.website.themeListing.selected_color_keys,
  weddingDate: getUserWeddingDate(state),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadTheme: () => {
    dispatch(getByKeyV2(ownProps.params.themeKey));
  },
  unloadTheme: () => dispatch(resetThemes()),
  setWeddingTheme: (themeId, onboard, weddingDate) => {
    dispatch(updateWeddingTheme(themeId));
    if (onboard) {
      dispatch(fetchWeddingAccount()).then(() => {
        dispatch(disableWebsiteOnboarding());
        const { BUSINESS_UNIT, COMPONENT } = onboardTrackingConstants;
        trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDING_WEBSITE, {
          isExistingUser: true,
          weddingDate,
        });
        browserHistory.push('/wedding/manage/website/pages/home');
      });
    } else {
      browserHistory.push('/wedding/manage/website/pages/home');
    }
  },
  storeThemeKey: themeKey => dispatch(storeCurrentThemeKey(themeKey)),
  openShare: wedding => {
    dispatch(showModal('SHARE', { wedding }, { size: 'md' }));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ThemeDetail);



// WEBPACK FOOTER //
// ./src/components/manage/website/ThemeDetail.jsx